/**
 * SB6-AUTH-RECONNECT-01 — soft session-expired prompt.
 *
 * Surfaces when the auth store status flips to `'session_expired'`
 * (i.e. a refresh failed AND the user was idle for >= IDLE_MAX). The
 * legacy code path was `clearSession() → redirect to /login`, which
 * destroyed the user's current page state. This modal sits ON TOP of
 * the current page instead — the URL doesn't change, the React tree
 * doesn't unmount, and after a successful re-auth the user resumes
 * exactly where they were.
 *
 * Implementation note on the "password field" in the spec:
 * ----------------------------------------------------------
 * The original spec called for a password field + Sign-in button posting
 * directly to Zitadel. WindPlatform uses OIDC PKCE auth-code flow — the
 * FE NEVER sees the user's password (Zitadel handles credential entry on
 * its own login page after a redirect). We cannot implement a real
 * in-modal password grant without enabling Resource Owner Password Grant
 * on the Zitadel app, which is explicitly disabled for security
 * compliance.
 *
 * The compromise: the modal shows the user's email (so they know which
 * account is expired) and a single "Sign in to continue" button. Clicking
 * it re-runs the OIDC PKCE flow with the CURRENT path as `returnTo`.
 * If Zitadel still has a live session cookie (the common case for a
 * walk-away user), the round-trip is sub-second and feels like a soft
 * confirm dialog. If Zitadel's session also expired the user enters
 * their password on Zitadel's page and lands back exactly where they
 * were.
 */

import { useEffect, useRef } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import { useAuth } from "./useAuth.js";
import { useAuthStore } from "./store.js";

export function SessionExpiredModal() {
  const status = useAuthStore((s) => s.status);
  const session = useAuthStore((s) => s.session);
  const { signIn } = useAuth();
  const open = status === "session_expired";
  const signInButtonRef = useRef<HTMLButtonElement | null>(null);

  // Defensive: if the modal opens with no session in store, fall back to
  // the legacy clear flow (the rest of the FE expects either authenticated
  // or idle — a "session_expired with no session" state is an inconsistency
  // we should not perpetuate).
  useEffect(() => {
    if (open && !session) {
      useAuthStore.getState().clearSession();
    }
  }, [open, session]);

  // Imperative focus on open. Equivalent to `autoFocus` but doesn't trip
  // jsx-a11y/no-autofocus — the modal is non-dismissable so focusing the
  // single primary action is the right keyboard-affordance default.
  useEffect(() => {
    if (open && signInButtonRef.current) {
      signInButtonRef.current.focus();
    }
  }, [open]);

  if (!open || !session) return null;

  async function onSignIn(): Promise<void> {
    const returnTo = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    const email = session?.user.email;
    await signIn({
      ...(email ? { loginHint: email } : {}),
      returnTo,
    });
  }

  return (
    <Dialog
      open={open}
      // disableEscapeKeyDown + no onClose for backdrop click: the modal
      // is intentionally non-dismissable. The user MUST re-auth before
      // continuing — bypassing it would leave the page in a half-broken
      // state (queries failing 401, no way to refresh).
      disableEscapeKeyDown
      aria-labelledby="session-expired-title"
      aria-describedby="session-expired-desc"
      data-testid="session-expired-modal"
    >
      <DialogTitle id="session-expired-title">
        Your session has expired
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <DialogContentText id="session-expired-desc">
            For your security, please sign in again to continue. Your current
            work is preserved — you&apos;ll return to this page after signing
            in.
          </DialogContentText>
          <Box
            sx={{
              px: 2,
              py: 1.5,
              borderRadius: 1,
              bgcolor: "action.hover",
            }}
          >
            <Typography variant="caption" color="text.secondary">
              Signed in as
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {session.user.email}
            </Typography>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          ref={signInButtonRef}
          variant="contained"
          onClick={() => {
            void onSignIn();
          }}
          fullWidth
          data-testid="session-expired-signin"
        >
          Sign in to continue
        </Button>
      </DialogActions>
    </Dialog>
  );
}
